import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert
} from '@mui/material';
import { TrendingUp, TrendingDown, MoreVert } from '@mui/icons-material';
import { backendApiService } from '../../services/backendApiService';

interface RankingChange {
  keyword: string;
  url: string;
  actualUrl?: string;
  previousPosition: number;
  currentPosition: number;
  change: number;
  traffic?: number;
}

interface TopGainersLosersProps {
  websiteId: string;
  country: string;
  dateRange?: {
    from: string;
    to: string;
  };
}

const TopGainersLosers: React.FC<TopGainersLosersProps> = ({ websiteId, country, dateRange }) => {
  const [gainers, setGainers] = useState<RankingChange[]>([]);
  const [losers, setLosers] = useState<RankingChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [timePeriod, setTimePeriod] = useState('7d');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  console.log('TopGainersLosers props:', { websiteId, country, typeOfWebsiteId: typeof websiteId, typeOfCountry: typeof country });

  const timePeriodOptions = [
    { value: '1d', label: 'Last 24 hours' },
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: '90d', label: 'Last 90 days' }
  ];

  useEffect(() => {
    fetchRankingChanges();
  }, [websiteId, country, timePeriod]);

  const fetchRankingChanges = async () => {
    if (!websiteId || !country) return;

    setLoading(true);
    setError(null);

    try {
      const response = await backendApiService.get('/api/rankings/changes', {
        websiteId,
        country,
        period: timePeriod,
        limit: 5
      });

      console.log('Rankings API response:', response);

      // Handle both response structures
      if (response.data && response.data.data) {
        // Axios response structure
        setGainers(response.data.data.gainers || []);
        setLosers(response.data.data.losers || []);
      } else if (response.success && response.data) {
        // Direct response structure
        setGainers(response.data.gainers || []);
        setLosers(response.data.losers || []);
      }
    } catch (err) {
      console.error('Error fetching ranking changes:', err);
      setError('Failed to load ranking changes');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTimePeriodChange = (value: string) => {
    setTimePeriod(value);
    handleMenuClose();
  };

  const formatChange = (change: number) => {
    const absChange = Math.abs(change);
    return change > 0 ? `+${absChange}` : `-${absChange}`;
  };

  const formatTraffic = (traffic?: number) => {
    if (!traffic) return '-';
    if (traffic >= 1000) {
      return `${(traffic / 1000).toFixed(1)}k`;
    }
    return traffic.toString();
  };

  const renderRankingItem = (item: RankingChange, type: 'gainer' | 'loser') => {
    const isGainer = type === 'gainer';
    const color = isGainer ? 'success' : 'error';
    const Icon = isGainer ? TrendingUp : TrendingDown;

    return (
      <Box key={item.keyword} sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <Icon sx={{ color: `${color}.main`, mr: 1, fontSize: 20 }} />
          <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
            {item.keyword}
          </Typography>
          <Chip
            label={formatChange(item.change)}
            size="small"
            color={color}
            sx={{ minWidth: 45 }}
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', pl: 3.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
            {item.previousPosition} → {item.currentPosition}
          </Typography>
          {item.traffic !== undefined && (
            <Typography variant="caption" color="text.secondary">
              {formatTraffic(item.traffic)} visits
            </Typography>
          )}
        </Box>
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ 
            pl: 3.5, 
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={item.actualUrl || item.url}
        >
          {item.actualUrl ? item.actualUrl.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '') : item.url}
        </Typography>
      </Box>
    );
  };

  if (loading) {
    return (
      <Card>
        <CardContent sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Top Movers
          </Typography>
          <IconButton size="small" onClick={handleMenuClick}>
            <MoreVert />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            {timePeriodOptions.map((option) => (
              <MenuItem
                key={option.value}
                selected={timePeriod === option.value}
                onClick={() => handleTimePeriodChange(option.value)}
              >
                {option.label}
              </MenuItem>
            ))}
          </Menu>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="success.main" sx={{ mb: 2 }}>
              Top Gainers (Top 5)
            </Typography>
            {gainers.length > 0 ? (
              gainers.map((item) => renderRankingItem(item, 'gainer'))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No significant gains in this period
              </Typography>
            )}
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="error.main" sx={{ mb: 2 }}>
              Top Losers (Top 5)
            </Typography>
            {losers.length > 0 ? (
              losers.map((item) => renderRankingItem(item, 'loser'))
            ) : (
              <Typography variant="body2" color="text.secondary">
                No significant losses in this period
              </Typography>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default TopGainersLosers;